import React, { createContext, useContext, useState, useCallback, useRef } from "react";

export const MinimizedWindowsContext = createContext(null);

export function MinimizedWindowsProvider({ children }) {
    const [minimizedWindows, setMinimizedWindows] = useState([]);
    const switchTabHandlerRef = useRef(null);

    const registerTabSwitcher = useCallback((handler) => {
        switchTabHandlerRef.current = handler;
    }, []);

    const minimizeWindow = useCallback((windowConfig) => {
        setMinimizedWindows((prev) => {
            const index = prev.findIndex((w) => w.id === windowConfig.id);
            if (index >= 0) {
                const updated = [...prev];
                updated[index] = { ...updated[index], ...windowConfig };
                return updated;
            }
            return [...prev, windowConfig];
        });
    }, []);

    const removeWindow = useCallback((id) => {
        setMinimizedWindows((prev) => prev.filter((w) => w.id !== id));
    }, []);

    const restoreWindow = useCallback((id) => {
        setMinimizedWindows((prev) => {
            const target = prev.find((w) => w.id === id);
            if (target) {
                if (target.tabKey && switchTabHandlerRef.current) {
                    try {
                        switchTabHandlerRef.current(target.tabKey);
                    } catch (e) {
                        console.error("Erro ao alternar aba ao restaurar janela:", e);
                    }
                }
                if (typeof target.onRestore === "function") {
                    target.onRestore();
                }
            }
            return prev.filter((w) => w.id !== id);
        });
    }, []);

    const closeWindow = useCallback((id) => {
        setMinimizedWindows((prev) => {
            const target = prev.find((w) => w.id === id);
            if (target && typeof target.onClose === "function") {
                target.onClose();
            }
            return prev.filter((w) => w.id !== id);
        });
    }, []);

    const isWindowMinimized = useCallback(
        (id) => minimizedWindows.some((w) => w.id === id),
        [minimizedWindows]
    );

    const value = {
        minimizedWindows,
        minimizeWindow,
        restoreWindow,
        closeWindow,
        removeWindow,
        isWindowMinimized,
        registerTabSwitcher
    };

    return (
        <MinimizedWindowsContext.Provider value={value}>
            {children}
        </MinimizedWindowsContext.Provider>
    );
}

export function useMinimizedWindows() {
    const context = useContext(MinimizedWindowsContext);
    if (!context) {
        throw new Error("useMinimizedWindows must be used within a MinimizedWindowsProvider");
    }
    return context;
}
